import L from "leaflet";
import type {
  LinePlacement,
  LinePlacementHeadline,
  LinePlacementSummary,
  LineStatusBucket,
  MdcpOrderMap,
  MdcpPhotoPoint,
  PlacedTypedLines,
} from "../types";

// ── line placement (kablowa/doziemna vs napowietrzna) ──────────────────────────
// Labels mirror backend line_placement.py so the order description, the map tooltip
// and the legend all say the same thing.
export const PLACEMENT_LABEL: Record<LinePlacement, string> = {
  underground: "kablowa (doziemna)",
  overhead: "napowietrzna",
  unknown: "nieokreślona",
};
export const HEADLINE_LABEL: Record<LinePlacementHeadline, string> = {
  underground: "Linia kablowa (doziemna)",
  overhead: "Linia napowietrzna",
  mixed: "Linia mieszana: kablowa (doziemna) i napowietrzna",
  unknown: "Rodzaj linii nieokreślony",
};
export const LINE_STATUS_LABEL: Record<LineStatusBucket, string> = {
  construction: "Budowa",
  demolition: "Demontaż",
  existing: "Istniejąca (nawiązanie)",
};
const STATUS_ORDER: LineStatusBucket[] = ["construction", "demolition", "existing"];

// Segment style: colour = status (red build / blue demolition / grey existing — the
// project map's colours), dash = placement (solid cable in the ground, "6 5" overhead —
// the ZE palette's own convention, "2 4" dotted when the slug says nothing).
const STATUS_COLOR: Record<LineStatusBucket, string> = {
  construction: "#dc2626",
  demolition: "#2563eb",
  existing: "#6b7280",
};
const PLACEMENT_DASH: Record<LinePlacement, string | undefined> = {
  underground: undefined,
  overhead: "6 5",
  unknown: "2 4",
};
export function placementLineStyle(placement: LinePlacement, status: LineStatusBucket): L.PathOptions {
  return {
    color: STATUS_COLOR[status],
    weight: status === "existing" ? 2 : 3,
    opacity: status === "existing" ? 0.75 : 1,
    dashArray: PLACEMENT_DASH[placement],
  };
}
// Legend swatch drawn with exactly the style the map paints the segment with.
export function placementSwatchSvg(placement: LinePlacement, status: LineStatusBucket): string {
  const st = placementLineStyle(placement, status);
  const dash = st.dashArray ? ` stroke-dasharray="${st.dashArray}"` : "";
  return (
    `<svg width="28" height="10" viewBox="0 0 28 10" xmlns="http://www.w3.org/2000/svg">` +
    `<line x1="1" y1="5" x2="27" y2="5" stroke="${st.color}" stroke-width="${st.weight}"` +
    ` stroke-opacity="${st.opacity ?? 1}"${dash}/></svg>`
  );
}
export function placementTooltip(placement: LinePlacement, status: LineStatusBucket): string {
  return `${HEADLINE_LABEL[placement]} — ${LINE_STATUS_LABEL[status].toLowerCase()}`;
}
/** "1 250 m" — whole metres with a Polish thousands separator. Hand-rolled grouping:
 *  toLocaleString("pl-PL") needs full ICU, which a slim Node (tests, CI) may not have. */
export function formatMetres(m: number): string {
  const whole = String(Math.round(m));
  return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, " ")} m`;
}
/** One legend line per status bucket, e.g. "Budowa: kablowa (doziemna) 320 m, napowietrzna 150 m". */
export function placementSummaryLines(summary: LinePlacementSummary | null | undefined): string[] {
  if (!summary || summary.rows.length === 0) return [];
  return STATUS_ORDER.flatMap((status) => {
    const rows = summary.rows.filter((r) => r.status === status);
    if (rows.length === 0) return [];
    const listed = rows.map((r) => `${PLACEMENT_LABEL[r.placement]} ${formatMetres(r.length_m)}`).join(", ");
    return [`${LINE_STATUS_LABEL[status]}: ${listed}`];
  });
}
/** Distinct (placement, status) pairs actually present — what the legend has to show. */
export function placementLegendEntries(
  summary: LinePlacementSummary | null | undefined,
): { placement: LinePlacement; status: LineStatusBucket }[] {
  if (!summary) return [];
  const seen = new Set<string>();
  const out: { placement: LinePlacement; status: LineStatusBucket }[] = [];
  for (const r of summary.rows) {
    const key = `${r.status}/${r.placement}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ placement: r.placement, status: r.status });
  }
  return out;
}

/** Draw the per-segment overlay (line kind + status) of a route. Returns the bounds of
 *  what was drawn, or null when the version has no typed layers (caller falls back to
 *  the flat geometry). typed_lines is a complete cover of the flat route, so nothing is
 *  laid underneath — a solid line would bleed through the dashed overhead style. */
export function drawPlacedLines(
  group: L.LayerGroup,
  typedLines: PlacedTypedLines | null | undefined,
  options: { interactive?: boolean } = {},
): L.LatLngBounds | null {
  const features = typedLines?.features ?? [];
  if (features.length === 0) return null;
  const interactive = options.interactive ?? true;
  let bounds: L.LatLngBounds | null = null;
  // Existing (reference) lines first so the job itself paints on top.
  const ordered = [...features].sort(
    (a, b) => (a.properties.status === "existing" ? 0 : 1) - (b.properties.status === "existing" ? 0 : 1),
  );
  for (const f of ordered) {
    const { placement, status } = f.properties;
    const layer = L.geoJSON(f as never, { style: placementLineStyle(placement, status), interactive });
    if (interactive) layer.bindTooltip(placementTooltip(placement, status), { sticky: true });
    layer.addTo(group);
    const b = layer.getBounds();
    if (b.isValid()) bounds = bounds ? bounds.extend(b) : b;
  }
  return bounds;
}

// Numbered circle + azimuth arrow (0° = north, clockwise). Green once it has a photo,
// otherwise pink; dashed ring when selected. Mirrors the project map's MDCP marker.
// Pick<>, bo z tej ikony korzysta też mapa zakresu w ogłoszeniu przetargowym (rfq),
// gdzie punkt to zamrożony snapshot bez zdjęć — nie pełny MdcpPhotoPoint.
export function mdcpPointIcon(
  p: Pick<MdcpPhotoPoint, "number" | "azimuth_deg" | "photos_count">,
  selected: boolean,
): L.DivIcon {
  const s = 36;
  const c = s / 2;
  const color = p.photos_count > 0 ? "#16a34a" : "#db2777";
  const ring = selected
    ? `<circle cx="${c}" cy="${c}" r="${c - 2}" fill="none" stroke="${color}" stroke-width="1.5" stroke-dasharray="3 3"/>`
    : "";
  return L.divIcon({
    html:
      `<svg width="${s}" height="${s}" viewBox="0 0 ${s} ${s}" xmlns="http://www.w3.org/2000/svg">` +
      `<g transform="rotate(${p.azimuth_deg}, ${c}, ${c})">` +
      `<line x1="${c}" y1="${c}" x2="${c}" y2="6" stroke="${color}" stroke-width="2.5"/>` +
      `<polygon points="${c},1 ${c - 4.5},8.5 ${c + 4.5},8.5" fill="${color}"/>` +
      `</g>${ring}` +
      `<circle cx="${c}" cy="${c}" r="9" fill="${color}" stroke="#fff" stroke-width="1.5"/>` +
      `<text x="${c}" y="${c + 3.5}" text-anchor="middle" font-size="10" font-weight="700" fill="#fff">${p.number}</text>` +
      `</svg>`,
    className: "pp-object-icon",
    iconSize: [s, s],
    iconAnchor: [c, c],
  });
}

/** View-only order-map context: the frozen MDCP buffer (orange), parcels (grey, tooltip with
 *  the parcel number) and the current route — per segment with its line kind (solid = cable in
 *  the ground, dashed = overhead; red build / blue demolition) when the version has typed
 *  layers, else the flat red route + blue dashed demolition. Returns the buffer/route bounds
 *  (if any) so callers can fit the view even before points exist. */
export function drawMdcpContext(
  group: L.LayerGroup,
  data: Pick<MdcpOrderMap, "route" | "parcels" | "buffer">,
): L.LatLngBounds | null {
  let bufferBounds: L.LatLngBounds | null = null;
  if (data.buffer?.geometry) {
    const buf = L.geoJSON(data.buffer.geometry as never, {
      style: { color: "#ea580c", weight: 2, fillColor: "#f97316", fillOpacity: 0.1 },
    });
    buf.bindTooltip("Bufor MDCP (zakres zlecenia)", { sticky: true });
    buf.addTo(group);
    const b = buf.getBounds();
    if (b.isValid()) bufferBounds = b;
  }
  for (const parcel of data.parcels) {
    if (!parcel.geometry) continue;
    const layer = L.geoJSON(parcel.geometry as never, {
      style: { color: "#555", weight: 1, fillColor: "#9ca3af", fillOpacity: 0.08 },
    });
    layer.bindTooltip(`dz. ${parcel.short_number || parcel.teryt}`, { sticky: true });
    layer.addTo(group);
  }
  let bounds: L.LatLngBounds | null = bufferBounds;
  // Per-segment overlay (kablowa/doziemna vs napowietrzna, budowa/demontaż) when the
  // version has typed layers; the flat red route + blue demolition only as the fallback
  // for old versions — exactly the project map's rule.
  const placed = drawPlacedLines(group, data.route?.typed_lines);
  if (placed) {
    bounds = bounds ? bounds.extend(placed) : placed;
    return bounds;
  }
  if (data.route?.geometry) {
    const route = L.geoJSON(data.route.geometry as never, {
      style: { color: "#dc2626", weight: 3 },
    });
    route.addTo(group);
    const b = route.getBounds();
    if (b.isValid()) bounds = bounds ? bounds.extend(b) : b;
  }
  if (data.route?.demolition_geometry) {
    L.geoJSON(data.route.demolition_geometry as never, {
      style: { color: "#2563eb", weight: 3, dashArray: "6 4" },
    }).addTo(group);
  }
  return bounds;
}

const PUWG_MERIDIAN: Record<number, string> = {
  2176: "15°E", 2177: "18°E", 2178: "21°E", 2179: "24°E",
};

export function puwgZoneLabel(srid: number): string {
  const m = PUWG_MERIDIAN[srid];
  return m ? `PUWG 2000/${m}` : `EPSG:${srid}`;
}

/** Surveyor-style coordinate readout: X = northing, Y = easting, in metres. */
export function formatCoords2000(c: MdcpPhotoPoint["coords_2000"]): string | null {
  if (!c) return null;
  return `X ${c.x.toFixed(2)} · Y ${c.y.toFixed(2)}`;
}

// ── „Zdjęcia z terenu": punkty MDCP z materiałem od geodety ────────────────────
// Zdjęcia punktów MDCP (MdcpPointPhoto) nie niosą geotagu: geodeta wgrywa je klikając
// w punkt na mapie, więc pozycją zdjęcia jest sam punkt — dokładniejszą niż EXIF, bo
// wprost wiadomo, który kadr należy do którego punktu. Warstwa „Zdjęcia z terenu"
// rysuje je obok geotagowanych plików wynikowych; bez tego cały materiał MDCP byłby
// na mapie niewidoczny (pliki bez GPS-a w EXIF-ie nie mają gdzie stanąć).

/** Punkty z jakimkolwiek materiałem (przyjętym LUB odrzuconym) i pozycją na mapie.
 *  Odrzucone też wchodzą: recenzent ma zobaczyć, co odpadło i dlaczego, a nie pustkę. */
export function mdcpPointsWithPhotos(points: MdcpPhotoPoint[]): MdcpPhotoPoint[] {
  return points.filter((p) => p.geometry != null && p.photos.length > 0);
}

/** Zielony = punkt ma materiał, który przeszedł odbiór; czerwony = wszystko odrzucone
 *  (punkt jest znów „bez zdjęcia" — do powtórzenia). Ta sama wymowa kolorów, co na
 *  pinezce geotagowanego pliku wynikowego. */
export function mdcpPhotoBadgeColor(p: MdcpPhotoPoint): string {
  return p.photos_count > 0 ? "#16a34a" : "#dc2626";
}

/** „1 odrzucone", „3 odrzucone", „5 odrzuconych" — polska liczba mnoga. */
function rejectedPlural(n: number): string {
  const few = n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14);
  return `${n} ${n === 1 || few ? "odrzucone" : "odrzuconych"}`;
}

/** Podsumowanie materiału punktu — wspólne dla dymka znacznika i nagłówka listy miniatur. */
function mdcpPhotoSummary(p: MdcpPhotoPoint): string {
  const parts = [
    p.photos_count > 0 ? `${p.photos_count} zdj. od geodety` : "brak przyjętego materiału",
  ];
  if (p.rejected_photos_count > 0) parts.push(rejectedPlural(p.rejected_photos_count));
  return parts.join(" · ");
}

export function mdcpPhotoBadgeTooltip(p: MdcpPhotoPoint): string {
  const parts = [`Punkt #${p.number}`, mdcpPhotoSummary(p)];
  if (p.note) parts.push(p.note);
  return `${parts.join(" · ")} — kliknij, aby zobaczyć zdjęcia`;
}

/** Znacznik aparatu z numerem punktu. Rysowany Z BOKU punktu (kotwica w lewym dolnym
 *  rogu + kreska do punktu), bo przy rozwiniętym panelu MDCP w tym samym miejscu stoi
 *  36-pikselowa pinezka punktu — wyśrodkowany znacznik schowałby się pod nią razem
 *  z kliknięciem otwierającym zdjęcia. Odsunięcie (31 px) jest dobrane tak, żeby koło
 *  znacznika minęło promień tamtej pinezki, a nie tylko wystawało zza niej. */
export function mdcpPhotoBadgeSvg(p: MdcpPhotoPoint): string {
  const color = mdcpPhotoBadgeColor(p);
  const label = String(p.number);
  const fs = label.length > 2 ? 6.5 : label.length > 1 ? 7.5 : 9;
  return (
    `<svg width="44" height="44" viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg">` +
    // Kreska łącząca znacznik z punktem — biała podkładka, żeby czytała się na każdym tle.
    // Zaczyna się 7 px od punktu, a nie w nim: przy włączonej warstwie punktów przecinałaby
    // numer wpisany w pinezkę MDCP.
    `<line x1="9" y1="35" x2="20" y2="24" stroke="#fff" stroke-width="4.5" stroke-linecap="round"/>` +
    `<line x1="9" y1="35" x2="20" y2="24" stroke="${color}" stroke-width="2" stroke-linecap="round"/>` +
    `<g transform="translate(14,6)">` +
    `<circle cx="12" cy="12" r="11" fill="${color}" stroke="#fff" stroke-width="1.5"/>` +
    `<path d="M7 9.5A1.5 1.5 0 0 1 8.5 8h1.2l.9-1.5h2.8l.9 1.5h1.2A1.5 1.5 0 0 1 17 9.5V15a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 7 15z" fill="none" stroke="#fff" stroke-width="1.4"/>` +
    `<circle cx="12" cy="12.3" r="2.3" fill="none" stroke="#fff" stroke-width="1.4"/>` +
    `</g>` +
    // Plakietka z numerem punktu — odróżnia znacznik od pinezki geotagowanego pliku
    // wynikowego, która numeru nie ma (nie należy do żadnego punktu).
    `<circle cx="35" cy="26" r="6.5" fill="#fff" stroke="${color}" stroke-width="1.5"/>` +
    `<text x="35" y="28.9" text-anchor="middle" font-size="${fs}" font-weight="700" fill="${color}">${label}</text>` +
    `</svg>`
  );
}

export function mdcpPhotoBadgeIcon(p: MdcpPhotoPoint): L.DivIcon {
  return L.divIcon({
    html: mdcpPhotoBadgeSvg(p),
    className: "pp-object-icon",
    iconSize: [44, 44],
    iconAnchor: [4, 40],
  });
}

const HTML_ESCAPE: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" };
// Nazwy plików i powody odrzucenia to wolny tekst — escape przed wklejeniem w HTML dymka.
const escHtml = (s: string | null | undefined): string =>
  String(s ?? "").replace(/[&<>"]/g, (c) => HTML_ESCAPE[c]);

/** Dymek punktu MDCP: LISTA miniatur materiału, bo na jeden punkt bywa kilka zdjęć —
 *  sam znacznik nie może więc otwierać pełnego ekranu, bo nie wiadomo byłoby którego
 *  kadru dotyczy. Każda miniatura niesie `data-photo-id`; klik w nią otwiera dokładnie
 *  to zdjęcie na pełnym ekranie, tak samo jak klik w miniaturę w panelu MDCP. */
export function mdcpPointPhotosPopupHtml(p: MdcpPhotoPoint): string {
  const accepted = p.photos.filter((ph) => !ph.is_rejected).length;
  let okIdx = 0;
  const thumbs = p.photos
    .map((ph) => {
      const url = ph.file?.url ?? null;
      if (!ph.is_rejected) okIdx += 1;
      const title = ph.is_rejected
        ? `Odrzucone${ph.rejection_reason ? `: ${ph.rejection_reason}` : ""} — ${ph.original_name}`
        : `Zdjęcie ${okIdx}/${accepted} — ${ph.original_name}`;
      const inner = url
        ? `<img src="${escHtml(url)}" alt="" loading="lazy" style="display:block;width:68px;height:68px;object-fit:cover"/>`
        : `<span style="display:flex;width:68px;height:68px;align-items:center;justify-content:center;font-size:9px;color:#6b7280">brak podglądu</span>`;
      const stamp = ph.is_rejected
        ? `<span style="position:absolute;left:0;right:0;bottom:0;background:#dc2626;color:#fff;font-size:9px;line-height:1.3;text-align:center">odrzucone</span>`
        : "";
      return (
        `<button type="button" data-photo-id="${escHtml(ph.id)}" title="${escHtml(title)}" ` +
        `style="position:relative;padding:0;line-height:0;cursor:pointer;background:#fff;border:2px solid ` +
        `${ph.is_rejected ? "#dc2626" : "#d1d5db"}">${inner}${stamp}</button>`
      );
    })
    .join("");
  return (
    `<div style="min-width:220px;font-size:12px">` +
    `<div style="font-weight:600">Punkt #${p.number} · ${p.azimuth_deg}°</div>` +
    `<div style="color:#6b7280">${escHtml(mdcpPhotoSummary(p))}</div>` +
    (p.note ? `<div style="margin-top:2px">${escHtml(p.note)}</div>` : "") +
    `<div style="display:flex;flex-wrap:wrap;gap:4px;margin-top:6px;max-height:224px;overflow:auto">${thumbs}</div>` +
    `<div style="margin-top:5px;color:#9ca3af">Kliknij miniaturę — podgląd na pełnym ekranie.</div>` +
    `</div>`
  );
}
